/**
 * Operator-facing names for fulfillment order statuses. The wire values never change; only what the
 * screens print does. CLOSED means a confirmed Daily Closing covered the order, so it reads "Closing
 * confirmed" and leaves the word "Closed" to the Order team's meaning: an order closed before it shipped.
 */
const STATUS_LABELS: Readonly<Record<string, string>> = {
  CLOSED: 'Closing confirmed',
};

/** The RESOLVED verdict the close-unshipped action leaves, told apart from a plain resolve by its kind. */
export const CLOSED_UNSHIPPED_LABEL = 'Closed';

export const fulfillmentStatusLabel = (status: string, resolutionKind?: string | null): string => {
  if (status === 'RESOLVED' && resolutionKind === 'CLOSE_UNSHIPPED') {
    return CLOSED_UNSHIPPED_LABEL;
  }
  return STATUS_LABELS[status] ?? status;
};

/** The wireframe tints the chip by what the order is: amber on hold, blue finished, green otherwise. */
export const wireframeStatusClass = (fulfillmentStatus: string | null, holdReason: string | null): string => {
  if (holdReason != null) {
    return 'st-hold';
  }
  if (fulfillmentStatus === 'CANCELLED') {
    return 'st-cancelled';
  }
  return fulfillmentStatus === 'CLOSED' || fulfillmentStatus === 'RESOLVED' ? 'st-completed' : 'st-processing';
};

/** A hold is orthogonal to the pipeline — an order can be READY and held — so it wins the chip's one line. */
export const titleChipLabel = (
  fulfillmentStatus: string | null,
  holdReason: string | null,
  resolutionKind?: string | null,
): string => {
  if (holdReason != null) {
    return 'ON HOLD';
  }
  return fulfillmentStatus ? fulfillmentStatusLabel(fulfillmentStatus, resolutionKind) : '';
};

/** The order statuses that have not reached outbound; the server closes only these as unshipped. */
export const CLOSABLE_UNSHIPPED_STATUSES = ['ORDERED', 'AWAITING_INBOUND', 'READY'];

export const canCloseUnshipped = (status: string): boolean => CLOSABLE_UNSHIPPED_STATUSES.includes(status);

/** The 409 body the close-unshipped route answers with; only the fields the screen words a refusal from. */
export interface CloseUnshippedRefusal {
  code?: string;
  shippedLines?: Array<{ lineNo: number; lineStatus: string }>;
  from?: string | null;
}

/**
 * Both refusals mean the order moved since the screen drew the menu, so each says what the operator
 * can do next. Undefined for any other answer, which leaves the server's own sentence standing.
 */
export const describeCloseUnshippedRefusal = (body: CloseUnshippedRefusal | undefined): string | undefined => {
  if (body?.code === 'ORDER_ALREADY_SHIPPED') {
    const named = (body.shippedLines ?? [])
      .map((line) => `line ${line.lineNo} is ${fulfillmentStatusLabel(line.lineStatus)}`)
      .join(', ');
    return `This order has already shipped${named ? ` (${named})` : ''}, so it cannot be closed. Cancel the outbound first.`;
  }
  if (body?.code === 'TRANSITION_NOT_ALLOWED') {
    const now = body.from ? ` — this one is now ${fulfillmentStatusLabel(body.from)}` : '';
    return `Only an ORDERED, AWAITING_INBOUND or READY order can be closed${now}.`;
  }
  return undefined;
};
